/**
 * DebugOverlayRenderer.ts
 *
 * Renders debug overlay visualizations on the canvas overlay layer.
 * Includes:
 * - Debug dashboard (via DebugDashboardRenderer)
 * - Line boxes with baseline/ascent/descent guides
 * - Caret map visualization
 * - Glyph boxes with whitespace badges and break markers
 * - Affected paragraph highlighting
 *
 * Extracted from EdgeDocsCanvas.V3.tsx to improve maintainability.
 */

import type { DebugSettings } from './DebugSettings';
import { drawDebugDashboard } from './DebugDashboardRenderer';
import { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';

/**
 * Parameters required to render debug overlays
 */
export interface DebugOverlayParams {
  /** The overlay canvas element to draw on */
  overlayCanvas: HTMLCanvasElement;
  /** The current WASM snapshot containing lines and caret map */
  currentSnapshot: any;
  /** Current debug settings controlling which overlays to show */
  debugSettings: DebugSettings;
  /** Document text for grapheme segmentation */
  documentText?: string;
  /** Set of affected paragraph IDs for highlighting */
  affectedParagraphs?: Set<string>;
  /** The input handler for dashboard rendering */
  inputHandler?: any;
  /** The typesetter client for dashboard rendering */
  typesetterClient?: TypesetterWorkerClient | null;
  /** Shared input context for dashboard rendering */
  sharedInputContext?: any;
  /** Scale factor for dashboard rendering */
  scale?: number;
  /** Snapshot version for dashboard rendering */
  snapshotVersion?: number;
}

/**
 * Renders all enabled debug overlays on the overlay canvas
 *
 * @param params - Configuration and data required for rendering
 */
export function renderDebugOverlays(params: DebugOverlayParams): void {
  const {
    overlayCanvas,
    currentSnapshot,
    debugSettings,
    documentText = '',
    affectedParagraphs = new Set(),
    inputHandler,
    typesetterClient,
    sharedInputContext,
    scale = 1,
    snapshotVersion = 0
  } = params;

  if (!overlayCanvas || !currentSnapshot) return;
  const ctx = overlayCanvas.getContext('2d');
  if (!ctx) return;

  const s = debugSettings;
  const totalLines = currentSnapshot.lines ? currentSnapshot.lines.length : 0;
  const linesSubset = currentSnapshot.lines || [];

  // Compute grapheme range for page
  const pageStartG = linesSubset.length > 0 ? (linesSubset[0].start_grapheme_index || 0) : 0;
  const pageEndG = linesSubset.length > 0 ? (linesSubset[linesSubset.length - 1].end_grapheme_index || 0) : 0;

  // 0. Show debug dashboard (if enabled)
  if (s.showDebugDashboard && inputHandler && typesetterClient) {
    drawDebugDashboard({
      overlayCanvas,
      currentSnapshot,
      inputHandler,
      typesetterClient,
      sharedInputContext,
      scale,
      snapshotVersion
    });
  }

  // 1. Show line boxes
  if (s.showLineBoxes && linesSubset.length > 0) {
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 128, 255, 0.6)';
    for (const line of linesSubset) {
      const x = (line.x_offset_fp || 0) / 64.0;
      const yTop = ((line.baseline_y_fp || 0) - (line.ascent_fp || 0)) / 64.0;
      const w = (line.width_fp || 0) / 64.0;
      const h = (line.height_fp || 0) / 64.0;
      if (w <= 0) continue;
      ctx.strokeRect(x, yTop, w, h);

      if (s.showBaselines) {
        const baselineY = (line.baseline_y_fp || 0) / 64.0;
        ctx.save();
        ctx.lineWidth = 1;
        // Baseline (blue)
        ctx.strokeStyle = 'rgba(0, 120, 215, 0.9)';
        ctx.beginPath();
        ctx.moveTo(x, baselineY);
        ctx.lineTo(x + w, baselineY);
        ctx.stroke();
        // Ascent (green)
        ctx.strokeStyle = 'rgba(0, 180, 0, 0.8)';
        const ascentY = baselineY - ((line.ascent_fp || 0) / 64.0);
        ctx.beginPath();
        ctx.moveTo(x, ascentY);
        ctx.lineTo(x + w, ascentY);
        ctx.stroke();
        // Descent (red)
        ctx.strokeStyle = 'rgba(220, 0, 0, 0.8)';
        const descentY = baselineY + ((line.descent_fp || 0) / 64.0);
        ctx.beginPath();
        ctx.moveTo(x, descentY);
        ctx.lineTo(x + w, descentY);
        ctx.stroke();
        ctx.restore();
      }
    }
    ctx.restore();
  }

  // 2. Show caret map
  if (s.showCaretMap && currentSnapshot.caret_map) {
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 0, 0, 0.6)';
    for (const c of currentSnapshot.caret_map) {
      const x = (c.x_fp || 0) / 64.0;
      const y = (c.y_fp || 0) / 64.0;
      ctx.beginPath();
      ctx.moveTo(x, y - 6);
      ctx.lineTo(x, y + 6);
      ctx.stroke();
    }
    ctx.restore();
  }

  // 3. Show glyph boxes
  if (s.showGlyphBoxes && linesSubset.length > 0) {
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 200, 0, 0.7)';
    const boxes: any[] = Array.isArray((currentSnapshot as any).debug_glyph_boxes)
      ? ((currentSnapshot as any).debug_glyph_boxes as any[]).filter((b: any) => b.char_index >= pageStartG && b.char_index < pageEndG)
      : [];

    // Build grapheme segments for badges
    const segmenter = new (Intl as any).Segmenter('en', { granularity: 'grapheme' });
    const graphemes = Array.from(segmenter.segment(documentText)) as Array<{ segment: string; index: number }>;

    for (const b of boxes) {
      // Stroke for every box; dashed when invisible
      if (!b.visible) {
        ctx.setLineDash([4, 3]);
      } else {
        ctx.setLineDash([]);
      }
      ctx.strokeRect(b.x, b.y, b.width, b.height);

      // Whitespace badges and break markers
      const g = graphemes[b.char_index]?.segment || '';
      let badge: string | null = null;
      if (g === ' ') badge = 'sp';
      else if (g === '\u00A0') badge = 'nbsp';
      else if (g === '\t') badge = 'tab';
      else if (g === '\u200B') badge = 'zwsp';
      else if (g === '\u00AD') badge = 'shy';

      if (badge && s.showWhitespaceBadges) {
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.font = '10px sans-serif';
        ctx.fillText(badge, b.x + 2, b.y + 10);
        ctx.restore();
      }

      if (badge && s.showBreakMarkers) {
        // Break marker (small triangle at top center)
        ctx.save();
        ctx.beginPath();
        const cx = b.x + b.width / 2;
        ctx.moveTo(cx, b.y + 2);
        ctx.lineTo(cx - 3, b.y + 8);
        ctx.lineTo(cx + 3, b.y + 8);
        ctx.closePath();
        ctx.fillStyle = 'rgba(0,120,215,0.7)';
        ctx.fill();
        ctx.restore();
      }
    }
    ctx.restore();
  }

  // 4. Highlight affected paragraphs (Phase 4)
  if (s.highlightAffectedParagraphs && affectedParagraphs.size > 0) {
    ctx.save();
    // Use semi-transparent yellow background for affected paragraphs
    ctx.fillStyle = 'rgba(255, 255, 0, 0.2)'; // Yellow with 20% opacity
    ctx.strokeStyle = 'rgba(255, 200, 0, 0.6)'; // Orange border
    ctx.lineWidth = 2;

    // For now, highlight all lines if any paragraph is affected
    // TODO: Map paragraph IDs to specific lines for precise highlighting
    if (linesSubset.length > 0) {
      // Find bounding box of all lines
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      for (const line of linesSubset) {
        const x = (line.x_offset_fp || 0) / 64.0;
        const yTop = ((line.baseline_y_fp || 0) - (line.ascent_fp || 0)) / 64.0;
        const w = (line.width_fp || 0) / 64.0;
        const h = (line.height_fp || 0) / 64.0;

        if (w > 0 && h > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, yTop);
          maxX = Math.max(maxX, x + w);
          maxY = Math.max(maxY, yTop + h);
        }
      }

      if (minX !== Infinity && minY !== Infinity) {
        // Draw highlight rectangle
        const padding = 2; // Small padding around affected area
        ctx.fillRect(
          minX - padding,
          minY - padding,
          (maxX - minX) + (padding * 2),
          (maxY - minY) + (padding * 2)
        );
        ctx.strokeRect(
          minX - padding,
          minY - padding,
          (maxX - minX) + (padding * 2),
          (maxY - minY) + (padding * 2)
        );

        // Add label showing affected paragraph count
        ctx.save();
        ctx.fillStyle = 'rgba(255, 140, 0, 0.9)'; // Orange text
        ctx.font = 'bold 11px sans-serif';
        ctx.fillText(
          `Affected: ${affectedParagraphs.size} paragraph(s)`,
          minX + 5,
          minY + 15
        );
        ctx.restore();
      }
    }

    ctx.restore();
  }
}
